import { useAuthStore } from '../stores/auth.js'

// Performance optimization: Response caching and request deduplication for time entries
class TimeEntryApiCache {
  constructor() {
    this.cache = new Map()
    this.pendingRequests = new Map()
    this.maxAge = 3 * 60 * 1000 // 3 minutes (shorter for time entries as they change more frequently)
    this.maxSize = 50
  }

  getCacheKey(url, options = {}) {
    return `${options.method || 'GET'}:${url}:${JSON.stringify(options.body || {})}`
  }

  get(url, options) {
    const key = this.getCacheKey(url, options)
    const cached = this.cache.get(key)
    
    if (cached && Date.now() - cached.timestamp < this.maxAge) {
      return cached.data
    }
    return null
  }

  set(url, options, data) {
    const key = this.getCacheKey(url, options)
    
    // Implement LRU eviction if cache is full
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value
      this.cache.delete(firstKey)
    }
    
    this.cache.set(key, {
      data: data,
      timestamp: Date.now()
    })
  }

  getOrCreateRequest(url, options, requestFn) {
    const key = this.getCacheKey(url, options)
    
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)
    }
    
    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key)
    })
    
    this.pendingRequests.set(key, promise)
    return promise
  }

  invalidate(pattern) {
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key)
      }
    }
  }

  clear() {
    this.cache.clear()
    this.pendingRequests.clear()
  }
}

const timeEntryCache = new TimeEntryApiCache()

// Calculate required break time based on shift duration (Handels kollektivavtal standards)
// Company policy based on Handels kollektivavtal practices:
// - 5+ hours → 30 minutes break
// - 7+ hours → 60 minutes break  
// - Over 8 hours → 75 minutes break
function calculateBreakTime(shiftDurationHours) {
  if (shiftDurationHours > 8) {
    return 75; // 75 minutes for shifts over 8 hours
  } else if (shiftDurationHours >= 7) {
    return 60; // 60 minutes for shifts 7-8 hours
  } else if (shiftDurationHours >= 5) {
    return 30; // 30 minutes for shifts 5+ hours
  }
  return 0; // No break required for shorter shifts
}

// Calculate shift duration and break times
function calculateShiftMetrics(entry) {
  if (!entry.start_time || !entry.end_time) {
    return entry;
  }

  try {
    const start = new Date(entry.start_time);
    const end = new Date(entry.end_time);
    
    // Calculate total shift duration in hours
    const durationMs = end - start;
    const durationHours = durationMs / (1000 * 60 * 60);
    
    // Calculate required break time
    const requiredBreakMinutes = calculateBreakTime(durationHours);
    
    // Add calculated fields to entry
    entry.shift_duration_hours = parseFloat(durationHours.toFixed(2));
    entry.required_break_minutes = requiredBreakMinutes;
    
    // Calculate net working hours (shift duration minus break time)
    const netWorkingHours = durationHours - (requiredBreakMinutes / 60);
    entry.net_working_hours = parseFloat(netWorkingHours.toFixed(2));
    
  } catch (error) {
    console.error('Error calculating shift metrics:', error);
  }
  
  return entry;
}

// Convert server time to local time format
function convertToLocal(entry) {
  // If backend provides start_time_local and end_time_local, use them directly
  if (entry.start_time_local && entry.end_time_local) {
    // Optionally, format to 'YYYY-MM-DD HH:mm:ss' if needed
    entry.start_time_local = entry.start_time_local.replace('T', ' ').slice(0, 19)
    entry.end_time_local = entry.end_time_local.replace('T', ' ').slice(0, 19)
  } else {
    // Fallback: try to construct Date from start_time and end_time if they look like ISO strings
    let startLocal = null;
    let endLocal = null;
    if (entry.start_time && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(entry.start_time)) {
      const start = new Date(entry.start_time);
      startLocal = start.toISOString().replace('T', ' ').slice(0, 19);
    }
    if (entry.end_time && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(entry.end_time)) {
      const end = new Date(entry.end_time);
      endLocal = end.toISOString().replace('T', ' ').slice(0, 19);
    }
    entry.start_time_local = startLocal || entry.start_time || null;
    entry.end_time_local = endLocal || entry.end_time || null;
  }
  
  // Calculate shift metrics including break times
  entry = calculateShiftMetrics(entry);
  
  return entry;
}

export const timeEntryApi = {
  // Get time entries for a specific month (cached)
  async getMonthlyEntries(year, month) {
    const url = `/api/time-entries/monthly/?year=${year}&month=${month}`
    const cached = timeEntryCache.get(url, { method: 'GET' })
    if (cached) return cached

    return timeEntryCache.getOrCreateRequest(url, { method: 'GET' }, async () => {
      const authStore = useAuthStore()
      const response = await authStore.makeAuthenticatedRequest(url, { method: 'GET' })
      const data = await response.json()
      const processedData = data.map(convertToLocal)
      timeEntryCache.set(url, { method: 'GET' }, processedData)
      return processedData
    })
  },

  // Get all time entries for current user (cached)
  async getAllEntries() {
    const url = `/api/time-entries/`
    const cached = timeEntryCache.get(url, { method: 'GET' })
    if (cached) return cached

    return timeEntryCache.getOrCreateRequest(url, { method: 'GET' }, async () => {
      const authStore = useAuthStore()
      const response = await authStore.makeAuthenticatedRequest(url, { method: 'GET' })
      const data = await response.json()
      const processedData = data.map(convertToLocal)
      timeEntryCache.set(url, { method: 'GET' }, processedData)
      return processedData
    })
  },

  // Get a specific time entry by ID (cached)
  async getEntry(id) {
    const url = `/api/time-entries/${id}/`
    const cached = timeEntryCache.get(url, { method: 'GET' })
    if (cached) return cached

    return timeEntryCache.getOrCreateRequest(url, { method: 'GET' }, async () => {
      const authStore = useAuthStore()
      const response = await authStore.makeAuthenticatedRequest(url, { method: 'GET' })
      const data = await response.json()
      const processedData = convertToLocal(data)
      timeEntryCache.set(url, { method: 'GET' }, processedData)
      return processedData
    })
  },

  // Create a new time entry (invalidates cache)
  async createEntry(entryData) {
    const authStore = useAuthStore()
    const response = await authStore.makeAuthenticatedRequest(`/api/time-entries/`, {
      method: 'POST',
      body: JSON.stringify(entryData)
    })
    const result = await response.json()
    
    // Invalidate relevant cache entries
    timeEntryCache.invalidate('/api/time-entries')
    
    return result
  },

  // Update an existing time entry (invalidates cache)
  async updateEntry(id, entryData) {
    const authStore = useAuthStore()
    const response = await authStore.makeAuthenticatedRequest(`/api/time-entries/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(entryData)
    })
    const result = await response.json()
    
    // Invalidate relevant cache entries
    timeEntryCache.invalidate('/api/time-entries')
    
    return result
  },

  // Partially update a time entry (invalidates cache)
  async patchEntry(id, entryData) {
    const authStore = useAuthStore()
    const response = await authStore.makeAuthenticatedRequest(`/api/time-entries/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(entryData)
    })
    const result = await response.json()
    
    // Invalidate relevant cache entries
    timeEntryCache.invalidate('/api/time-entries')
    
    return result
  },

  // Delete a time entry (invalidates cache)
  async deleteEntry(id) {
    const authStore = useAuthStore()
    const response = await authStore.makeAuthenticatedRequest(`/api/time-entries/${id}/`, {
      method: 'DELETE'
    })
    const result = response.status === 204 ? null : await response.json()
    
    // Invalidate relevant cache entries
    timeEntryCache.invalidate('/api/time-entries')
    
    return result
  },

  // Create or update entry for a specific date (upsert operation)
  async saveEntryForDate(date, entryData) {
    try {
      // First, try to get existing entries for the month to find if entry exists
      const dateObj = new Date(date)
      const year = dateObj.getFullYear()
      const month = dateObj.getMonth() + 1
      
      const monthlyEntries = await this.getMonthlyEntries(year, month)
      const existingEntry = monthlyEntries.find(entry => entry.date === date)
      
      if (existingEntry) {
        // Update existing entry - don't include date field for updates
        const updateData = { ...entryData }
        delete updateData.date  // Remove date field to avoid validation issues
        return await this.updateEntry(existingEntry.id, updateData)
      } else {
        // Create new entry - include date field for creation
        return await this.createEntry({ ...entryData, date })
      }
    } catch (error) {
      console.error('Error saving time entry:', error)
      throw error
    }
  },

  // Manager method to update shift status (invalidates cache)
  async updateShiftStatus(shiftId, status, workerId = null) {
    const authStore = useAuthStore()
    let result
    
    if (workerId) {
      // Use manager-specific endpoint if workerId is provided
      const response = await authStore.makeAuthenticatedRequest(`/api/manager/${workerId}/worker-shifts/${shiftId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ status: status })
      })
      result = await response.json()
    } else {
      // Use regular endpoint for user's own shifts
      const response = await authStore.makeAuthenticatedRequest(`/api/time-entries/${shiftId}/`, {
        method: 'PATCH',
        body: JSON.stringify({ status: status })
      })
      result = await response.json()
    }
    
    // Invalidate relevant cache entries
    timeEntryCache.invalidate('/api/time-entries')
    timeEntryCache.invalidate('/api/manager')
    
    return result
  },

  // Get time entries with filters (cached for manager/planner dashboard)
  async getTimeEntries(params = {}) {
    // Create a normalized query string for caching
    const sortedParams = Object.entries(params)
      .filter(([_, v]) => v !== undefined && v !== null)
      .sort(([a], [b]) => a.localeCompare(b)) // Sort for consistent cache keys
    
    const query = sortedParams
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&')
    
    const url = `/api/time-entries/?${query}`
    const cached = timeEntryCache.get(url, { method: 'GET' })
    if (cached) return cached

    return timeEntryCache.getOrCreateRequest(url, { method: 'GET' }, async () => {
      const authStore = useAuthStore()
      const response = await authStore.makeAuthenticatedRequest(url, { method: 'GET' })
      if (!response.ok) throw new Error('Failed to load time entries')
      
      const data = await response.json()
      const processedData = data.map(convertToLocal)
      timeEntryCache.set(url, { method: 'GET' }, processedData)
      return processedData
    })
  },

  // Clear cache (useful for force refresh)
  clearCache() {
    timeEntryCache.clear()
  }
}